// The tutorial: one scripted solo match against a bot rival that walks a new
// player through every basic rule. The SERVER owns progress (which step the
// learner is on, `GameState.tutorialStep`) — it advances a step the moment
// its goal shows on the board, and a skip has the server play the step's
// canonical move for the learner so the lesson stays on the rails. The
// client only renders the step card and glows the step's targets.
//
// Copy lives here so the client and the server's step logic (keyed by `id`)
// can never disagree on what step N is.
/** Seat key of the scripted rival (it is never a real account). */
export const TUTORIAL_RIVAL_ID = "tutorial-rival";
export const TUTORIAL_RIVAL_NAME = "Rival";
export const TUTORIAL_STEPS = [
    {
        id: "welcome",
        title: "Welcome to Fortress",
        readOnly: true,
        body: [
            "Everything is built on NODES — the corners of the hexes.",
            "Climb, build fortresses, implement a WIN card. Any step can be skipped.",
        ],
    },
    {
        id: "place_home",
        title: "Place your home base",
        body: ["Your home is a free Mine. Click the glowing node, then confirm."],
    },
    {
        id: "set_production",
        title: "Split your production",
        body: [
            "You collect production every round. Split 5 points; put at least 1 in Obsidian.",
        ],
    },
    {
        id: "round_start_cycle",
        title: "Round-start market swap",
        body: [
            "The First-Turn marker holder may swap market cards for free each round.",
            "Keep both rows and confirm. (Cycling mid-turn costs 1 Food per card and an action.)",
        ],
    },
    {
        id: "build_scout",
        title: "Build a Scout",
        spotlight: "construct_building",
        body: [
            "2 actions per turn. Construct → Scout on the glowing node: it holds a ONE-TIME resource.",
            "Builds must be in range of your buildings, along unbroken paths. Scouts reach 7.",
        ],
    },
    {
        id: "end_turn",
        title: "End your turn",
        spotlight: "end_turn",
        body: [
            "END TURN hands over but keeps your place in the round. Spending your last action ends the turn too.",
        ],
    },
    {
        id: "pass_round",
        title: "Pass",
        spotlight: "pass",
        body: [
            "On a fresh turn you may PASS: out until next round. First to pass leads the next round.",
        ],
    },
    {
        id: "build_mine",
        title: "Build a Mine",
        spotlight: "construct_building",
        body: [
            "The glowing node holds a PERMANENT resource. Only a Mine taps it: +1 production every round. Mine range is 2.",
        ],
    },
    {
        id: "climb_ridge",
        title: "Climb into the ridgeline",
        spotlight: "construct_building",
        body: [
            "Higher zones pay bonus resources but cost extra Vigor: ridgeline 1, crag 3, summit 5.",
            "Scout up to the glowing node until you stand in the ridgeline.",
        ],
    },
    {
        id: "attack",
        title: "Attack",
        spotlight: "construct_building",
        body: [
            "Construct → Barracks on the glowing node, aimed at the glowing rival Scout (attack range 3).",
            "At turn end, attackers must OUTNUMBER defenders. Ties survive.",
        ],
    },
    {
        id: "end_turn_combat",
        title: "Let it land",
        spotlight: "end_turn",
        body: [
            "End your turn: the Scout falls. STEAL 1 resource, then pay your Barracks' Food upkeep.",
        ],
    },
    {
        id: "defend",
        title: "Defend",
        spotlight: "pass",
        body: [
            "Pass on fresh turns; your rival will strike back.",
            "In the DEFENSE WINDOW, aim your Barracks to defend (range 4), then confirm. 1 vs 1 is a tie — you survive.",
        ],
    },
    {
        id: "bridge",
        title: "Build a Bridge",
        spotlight: "construct_bridge",
        body: [
            "Chasms (barred paths) block building and range until bridged. Build on the glowing path — one end in range is enough.",
        ],
    },
    {
        id: "sphinx_piece",
        title: "Complete a pattern",
        spotlight: "construct_building",
        body: [
            "Cards are PATTERNS: ● Scout, ▲ Barracks, ■ Mine, ★ any, X empty, thick edge = bridge.",
            "A Scout on the glowing node completes Trifecta Sphinx.",
        ],
    },
    {
        id: "implement_sphinx",
        title: "Implement Trifecta Sphinx",
        spotlight: "implement_fortress",
        body: [
            "Implement → Trifecta Sphinx, pay its Obsidian. Fortresses are indestructible and grant effects (effects tray).",
        ],
    },
    {
        id: "climb_summit",
        title: "Climb to the summit",
        spotlight: "construct_building",
        body: [
            "Piercing Needle must stand entirely in the SUMMIT. Scout up to the glowing node; crag costs 3 Vigor, summit 5.",
        ],
    },
    {
        id: "needle_barracks",
        title: "Needle: the Barracks",
        spotlight: "construct_building",
        body: [
            "The Needle: a Barracks, any building 2 steps away, a bridge between. Build the Barracks on the glowing node.",
        ],
    },
    {
        id: "needle_scout",
        title: "Needle: the star",
        spotlight: "construct_building",
        body: ["Any building fills the star. Build a Scout on the glowing node."],
    },
    {
        id: "needle_bridge",
        title: "Needle: the bridge",
        spotlight: "construct_bridge",
        body: ["Build the pattern's bridge on the glowing path."],
    },
    {
        id: "implement_needle",
        title: "Implement and win",
        spotlight: "implement_fortress",
        body: [
            "Implement → Piercing Needle. Win cards end the game as printed — this one at once.",
        ],
    },
    {
        id: "done",
        title: "You know the basics",
        readOnly: true,
        body: [
            "Nodes, zones, resources, the five actions, combat, patterns, win cards.",
            "Queued moves, card effects and more: see the rulebook (?).",
        ],
    },
];
export const TUTORIAL_STEP_INDEX = Object.fromEntries(TUTORIAL_STEPS.map((s, i) => [s.id, i]));
//# sourceMappingURL=tutorial.js.map